/**
 * CSV download for the list screens.
 *
 * A plain `<a href="/api/…/export" download>` would be simpler, but it gives the
 * screen nothing to work with: no way to disable the button while the Worker is
 * still building the file, and a failed request (expired session, 500) would
 * land on disk as a .csv full of JSON. So the file is fetched, checked and only
 * then handed to an anchor as a blob URL.
 *
 * `credentials: 'same-origin'` is what carries the session cookie; the request
 * is a GET, so it never trips the Worker's origin CSRF check.
 */

import { useCallback, useRef, useState } from 'react'

/** Values a screen passes as filters. Empty ones are simply left out. */
export type DownloadParams = Record<string, string | number | boolean | null | undefined>

const BASE_URL = '/api'

function buildQuery(params: DownloadParams): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '' || value === false) continue
    search.set(key, value === true ? '1' : String(value))
  }
  const query = search.toString()
  return query === '' ? '' : `?${query}`
}

/**
 * Content-Disposition → file name. The Worker always sends one; the fallback
 * only covers a proxy stripping the header.
 */
function filenameOf(header: string | null, fallback: string): string {
  const match = header ? /filename="([^"]+)"/i.exec(header) : null
  return match?.[1] ?? fallback
}

async function errorMessageOf(response: Response): Promise<string> {
  if (response.status === 401) return 'Oturum süresi doldu. Yeniden giriş yapın.'
  try {
    const body: unknown = await response.json()
    if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') {
      return body.error
    }
  } catch {
    // Not JSON — fall through to the generic message.
  }
  return `Dışa aktarma başarısız oldu (${response.status}).`
}

/**
 * Fetches `path` with the given filters and saves the response as a file.
 * Throws with a Turkish message the screen can show as-is.
 */
export async function downloadCsv(path: string, params: DownloadParams = {}): Promise<void> {
  const response = await fetch(`${BASE_URL}${path}${buildQuery(params)}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'text/csv' },
  })

  if (!response.ok) throw new Error(await errorMessageOf(response))

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filenameOf(response.headers.get('Content-Disposition'), 'panel-liste.csv')
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  // Revoked on the next tick: Safari aborts the download if the URL dies first.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export interface CsvDownload {
  /** True while a file is being built — the button stays disabled meanwhile. */
  busy: boolean
  /** Turkish message from the last failed attempt, or null. */
  error: string | null
  start: (path: string, params?: DownloadParams) => void
}

/** Button state for the "Dışa aktar" control. */
export function useCsvDownload(): CsvDownload {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Guards against a double click racing past the disabled attribute.
  const running = useRef(false)

  const start = useCallback((path: string, params: DownloadParams = {}) => {
    if (running.current) return
    running.current = true
    setBusy(true)
    setError(null)
    void downloadCsv(path, params)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Dışa aktarma başarısız oldu.')
      })
      .finally(() => {
        running.current = false
        setBusy(false)
      })
  }, [])

  return { busy, error, start }
}
